import React, { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { WorkflowStatus, TaskType, Priority } from '@/api/mastersApi';

export interface FilterDrawerProps {
  isOpen: boolean;
  anchorRef: React.RefObject<HTMLElement | HTMLDivElement | HTMLButtonElement>;
  onClose: () => void;

  workflowStatuses: WorkflowStatus[];
  taskTypes: TaskType[];
  priorities: Priority[];
  sprints: Array<{ id: string; name: string }>;
  members: Array<{ user_id: string; user_display_name?: string; username?: string; user_email?: string }>;
  allLabels: string[];

  selectedStatusIds: string[]; setSelectedStatusIds: (v: string[]) => void;
  selectedPriorityIds: string[]; setSelectedPriorityIds: (v: string[]) => void;
  selectedTaskTypeIds: string[]; setSelectedTaskTypeIds: (v: string[]) => void;
  selectedSprintIds: string[]; setSelectedSprintIds: (v: string[]) => void;
  selectedAssigneeIds: string[]; setSelectedAssigneeIds: (v: string[]) => void;
  selectedLabels: string[]; setSelectedLabels: (v: string[]) => void;
  parentOnly: boolean; setParentOnly: (v: boolean) => void;
  subtaskOnly: boolean; setSubtaskOnly: (v: boolean) => void;
}

export const FilterDrawer: React.FC<FilterDrawerProps> = (props) => {
  const {
    isOpen, anchorRef, onClose,
    workflowStatuses, taskTypes, priorities, sprints, members, allLabels,
    selectedStatusIds, setSelectedStatusIds,
    selectedPriorityIds, setSelectedPriorityIds,
    selectedTaskTypeIds, setSelectedTaskTypeIds,
    selectedSprintIds, setSelectedSprintIds,
    selectedAssigneeIds, setSelectedAssigneeIds,
    selectedLabels, setSelectedLabels,
    parentOnly, setParentOnly,
    subtaskOnly, setSubtaskOnly,
  } = props;

  const ref = useRef<HTMLDivElement | null>(null);
  const [left, setLeft] = useState(0);
  const [top, setTop] = useState(0);
  const [width, setWidth] = useState(680);
  const [filterSearch, setFilterSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'Parent'|'Sprint'|'Assignee'|'Work type'|'Labels'|'Status'|'Priority'>('Status');

  useEffect(() => {
    const updatePos = () => {
      if (!isOpen) return;
      const rect = (anchorRef.current as any)?.getBoundingClientRect?.();
      if (!rect) return;
      const desired = Math.min(680, Math.max(360, window.innerWidth - 24));
      setWidth(desired);
      let l = Math.max(12, Math.min(rect.left, window.innerWidth - desired - 12));
      setLeft(Math.round(l));
      setTop(Math.round(rect.bottom + 8));
    };
    const onClick = (e: MouseEvent) => {
      if (!isOpen) return;
      if (ref.current && !ref.current.contains(e.target as Node) && anchorRef.current && !(anchorRef.current as any).contains?.(e.target)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    updatePos();
    document.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [isOpen, anchorRef, onClose]);

  const renderAssigneeName = (m: any) => m?.user_display_name || m?.username || m?.user_email || 'User';

  if (!isOpen) return null;

  return (
    <div ref={ref} className="fixed z-40 bg-white border border-gray-200 rounded-md shadow-xl" style={{ left, top, width, maxWidth: 'calc(100vw - 24px)' }}>
      <div className="flex h-[460px]">
        <div className="w-56 border-r border-gray-200 p-3 overflow-y-auto bg-gray-50/60">
          {['Parent','Sprint','Assignee','Work type','Labels','Status','Priority'].map((tab) => (
            <div key={tab} onClick={() => { setActiveTab(tab as any); setFilterSearch(''); }} className={`px-3 py-2 rounded cursor-pointer text-sm ${activeTab === tab ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-100 text-gray-700'}`}>{tab}</div>
          ))}
        </div>
        <div className="flex-1 p-4 flex flex-col">
          <div className="relative mb-3">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input className="w-full h-8 pl-10 pr-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder={`Search ${activeTab.toLowerCase()}`} value={filterSearch} onChange={(e)=>setFilterSearch(e.target.value)} />
          </div>
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'Parent' && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" className="rounded" checked={parentOnly} onChange={(e)=>props.setParentOnly(e.target.checked)} /> Parent issues only</label>
                <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" className="rounded" checked={subtaskOnly} onChange={(e)=>props.setSubtaskOnly(e.target.checked)} /> Subtasks only</label>
              </div>
            )}
            {activeTab === 'Sprint' && (
              <div className="space-y-2">{sprints.filter(s=> (s?.name||'').toLowerCase().includes(filterSearch.toLowerCase())).map((s)=> (
                <label key={s.id} className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" className="rounded" checked={selectedSprintIds.includes(String(s.id))} onChange={(e)=> setSelectedSprintIds(e.target.checked ? [...selectedSprintIds, String(s.id)] : selectedSprintIds.filter(id=>id!==String(s.id)))} /> {s.name}</label>
              ))}</div>
            )}
            {activeTab === 'Assignee' && (
              <div className="space-y-2">{members.filter(m=> renderAssigneeName(m).toLowerCase().includes(filterSearch.toLowerCase())).map((m)=> {
                const uid = String((m as any).user_id);
                const name = renderAssigneeName(m);
                return (
                  <label key={uid} className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" className="rounded" checked={selectedAssigneeIds.includes(uid)} onChange={(e)=> setSelectedAssigneeIds(e.target.checked ? [...selectedAssigneeIds, uid] : selectedAssigneeIds.filter(id=>id!==uid))} /> {name}</label>
                );
              })}</div>
            )}
            {activeTab === 'Work type' && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">{taskTypes.filter(t=> (t?.name||'').toLowerCase().includes(filterSearch.toLowerCase())).map((t)=> (
                <label key={t.id} className="flex items-center justify-between gap-2 text-sm text-gray-700 px-2 py-1 rounded hover:bg-gray-50"><input type="checkbox" className="rounded" checked={selectedTaskTypeIds.includes(String(t.id))} onChange={(e)=> setSelectedTaskTypeIds(e.target.checked ? [...selectedTaskTypeIds, String(t.id)] : selectedTaskTypeIds.filter(id=>id!==String(t.id)))} /> <span className="flex-1 truncate">{t.name}</span></label>
              ))}</div>
            )}
            {activeTab === 'Labels' && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">{allLabels.filter(l=> l.toLowerCase().includes(filterSearch.toLowerCase())).map((l)=> (
                <label key={l} className="flex items-center justify-between gap-2 text-sm text-gray-700 px-2 py-1 rounded hover:bg-gray-50"><input type="checkbox" className="rounded" checked={selectedLabels.includes(l)} onChange={(e)=> setSelectedLabels(e.target.checked ? [...selectedLabels, l] : selectedLabels.filter(id=>id!==l))} /> <span className="flex-1 truncate">{l}</span></label>
              ))}</div>
            )}
            {activeTab === 'Status' && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">{workflowStatuses.filter(s=> (s?.name||'').toLowerCase().includes(filterSearch.toLowerCase())).map((s)=> (
                <label key={s.id} className="flex items-center justify-between gap-2 text-sm text-gray-700 px-2 py-1 rounded hover:bg-gray-50"><input type="checkbox" className="rounded" checked={selectedStatusIds.includes(String(s.id))} onChange={(e)=> setSelectedStatusIds(e.target.checked ? [...selectedStatusIds, String(s.id)] : selectedStatusIds.filter(id=>id!==String(s.id)))} /> <span className="flex-1 truncate">{s.name}</span></label>
              ))}</div>
            )}
            {activeTab === 'Priority' && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">{priorities.filter(p=> (p?.name||'').toLowerCase().includes(filterSearch.toLowerCase())).map((p)=> (
                <label key={p.id} className="flex items-center justify-between gap-2 text-sm text-gray-700 px-2 py-1 rounded hover:bg-gray-50"><input type="checkbox" className="rounded" checked={selectedPriorityIds.includes(String(p.id))} onChange={(e)=> setSelectedPriorityIds(e.target.checked ? [...selectedPriorityIds, String(p.id)] : selectedPriorityIds.filter(id=>id!==String(p.id)))} /> <span className="flex-1 truncate">{p.name}</span></label>
              ))}</div>
            )}
          </div>
          <div className="pt-3 mt-3 border-t border-gray-200 flex items-center justify-between sticky bottom-0 bg-white">
            <div className="text-[11px] text-gray-500">Press <span className="font-semibold">Shift</span> + <span className="font-semibold">F</span> to open and close</div>
            <div className="flex items-center gap-2">
              <button className="h-8 px-3 rounded-md border border-gray-300 text-gray-700 text-sm hover:bg-gray-50" onClick={()=>{ setSelectedStatusIds([]); setSelectedPriorityIds([]); setSelectedTaskTypeIds([]); setSelectedAssigneeIds([]); setSelectedSprintIds([]); setSelectedLabels([]); setParentOnly(false); setSubtaskOnly(false); }}>Clear all</button>
              <button className="h-8 px-3 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700" onClick={onClose}>Done</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterDrawer;



